<template>
  <div
    class="manager-detail-modern"
    role="main"
    aria-label="Détail du membre du personnel logistique"
  >
    <!-- Modern Skeleton Loading State -->
    <div
      v-if="isLoading"
      class="skeleton-loading"
      aria-live="polite"
      aria-busy="true"
      role="status"
    >
      <!-- Skeleton Page Header -->
      <div class="skeleton-header mb-4">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <div class="skeleton-title mb-2" />
            <div class="skeleton-subtitle" />
          </div>
          <div class="skeleton-actions">
            <div class="skeleton-btn mr-2" />
            <div class="skeleton-btn mr-2" />
            <div class="skeleton-btn" />
          </div>
        </div>
      </div>

      <!-- Skeleton Profile Layout -->
      <b-row class="mb-4">
        <b-col lg="4">
          <!-- Skeleton Profile Card -->
          <div class="skeleton-profile-card">
            <div class="skeleton-profile-header">
              <div class="skeleton-avatar" />
              <div class="skeleton-name" />
              <div class="skeleton-role" />
              <div class="skeleton-status" />
            </div>
            <div class="skeleton-profile-stats">
              <div class="skeleton-stat-item">
                <div class="skeleton-stat-icon" />
                <div class="skeleton-stat-content">
                  <div class="skeleton-stat-label" />
                  <div class="skeleton-stat-value" />
                </div>
              </div>
              <div class="skeleton-stat-item">
                <div class="skeleton-stat-icon" />
                <div class="skeleton-stat-content">
                  <div class="skeleton-stat-label" />
                  <div class="skeleton-stat-value" />
                </div>
              </div>
            </div>
          </div>
        </b-col>

        <b-col lg="8">
          <!-- Skeleton Information Cards -->
          <div class="skeleton-info-grid">
            <!-- Contact Info Card -->
            <div class="skeleton-info-card">
              <div class="skeleton-card-header">
                <div class="skeleton-card-icon" />
                <div class="skeleton-card-title" />
              </div>
              <div class="skeleton-card-body">
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Work Info Card -->
            <div class="skeleton-info-card">
              <div class="skeleton-card-header">
                <div class="skeleton-card-icon" />
                <div class="skeleton-card-title" />
              </div>
              <div class="skeleton-card-body">
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Personal Info Card -->
            <div class="skeleton-info-card skeleton-info-card-full">
              <div class="skeleton-card-header">
                <div class="skeleton-card-icon" />
                <div class="skeleton-card-title" />
              </div>
              <div class="skeleton-card-body">
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value" />
                  </div>
                </div>
                <div class="skeleton-info-item">
                  <div class="skeleton-info-icon" />
                  <div class="skeleton-info-content">
                    <div class="skeleton-info-label" />
                    <div class="skeleton-info-value skeleton-info-value-long" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="error-container"
      aria-live="assertive"
      role="alert"
    >
      <div class="error-content">
        <div class="error-icon">
          <feather-icon
            icon="AlertCircleIcon"
            size="48"
          />
        </div>
        <h3 class="error-title">
          Erreur
        </h3>
        <p class="error-message">
          {{ error }}
        </p>
      </div>
    </div>

    <!-- Content loaded successfully -->
    <div v-else>
      <!-- Modern Page Header -->
      <div class="page-header mb-4">
        <!-- Accessible page title and description for screen readers -->
        <h2
          id="staff-profile-title"
          class="sr-only"
        >
          Profil du membre du personnel logistique
        </h2>
        <p
          id="staff-profile-desc"
          class="sr-only"
        >
          Informations détaillées et gestion du membre du personnel logistique.
        </p>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h2 class="page-title mb-1">
              Profil du Personnel
            </h2>
            <p class="page-subtitle text-muted mb-0">
              Informations détaillées et gestion du membre du personnel
            </p>
          </div>
          <div class="header-actions">
            <b-button
              variant="warning"
              size="lg"
              class="btn-modern mr-2"
              :to="{ name: 'staff-edit', params: { id: staff.id }}"
            >
              <feather-icon
                icon="EditIcon"
                size="16"
                class="mr-2"
              />
              Modifier
            </b-button>
            <b-button
              :variant="staff.is_active ? 'danger' : 'success'"
              size="lg"
              class="btn-modern"
              @click="toggleStaffStatus"
            >
              <feather-icon
                :icon="staff.is_active ? 'XCircleIcon' : 'CheckCircleIcon'"
                size="16"
                class="mr-2"
              />
              {{ staff.is_active ? 'Désactiver' : 'Activer' }}
            </b-button>
          </div>
        </div>
      </div>

      <!-- Modern Profile Card -->
      <b-row class="mb-4">
        <b-col lg="4">
          <div class="profile-card-modern">
            <div class="profile-header">
              <div class="profile-avatar">
                <div class="avatar-circle-large">
                  <span class="avatar-initials-large">{{ getInitials(staff.username) }}</span>
                </div>
                <div
                  class="status-indicator-large"
                  :class="{ active: staff.is_active }"
                />
              </div>
              <h3 class="profile-name">
                {{ staff.username }}
              </h3>
              <p class="profile-role">
                {{ staff.role || 'Personnel' }}
              </p>
              <div class="profile-status">
                <span
                  class="status-badge-large"
                  :class="staff.is_active ? 'active' : 'inactive'"
                >
                  {{ staff.is_active ? 'Actif' : 'Inactif' }}
                </span>
              </div>
            </div>

            <div class="profile-stats">
              <div class="stat-item">
                <div class="stat-icon">
                  <feather-icon
                    icon="ClockIcon"
                    size="20"
                  />
                </div>
                <div class="stat-content">
                  <span class="stat-label">Créé le</span>
                  <span class="stat-value">{{ formatDate(staff.created_at) }}</span>
                </div>
              </div>
              <div class="stat-item">
                <div class="stat-icon">
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="20"
                  />
                </div>
                <div class="stat-content">
                  <span class="stat-label">Modifié le</span>
                  <span class="stat-value">{{ formatDate(staff.updated_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </b-col>

        <b-col lg="8">
          <!-- Information Cards Grid -->
          <div class="info-cards-grid">
            <!-- Contact Information -->
            <div class="info-card">
              <div class="info-card-header">
                <div class="info-card-icon contact">
                  <feather-icon
                    icon="MailIcon"
                    size="24"
                  />
                </div>
                <h5 class="info-card-title">
                  Informations de Contact
                </h5>
              </div>
              <div class="info-card-body">
                <div class="info-item">
                  <feather-icon
                    icon="MailIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Email</span>
                    <span class="info-value">{{ staff.email || 'Non défini' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <feather-icon
                    icon="PhoneIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Téléphone</span>
                    <span class="info-value">{{ staff.phone || 'Non défini' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Work Information -->
            <div class="info-card">
              <div class="info-card-header">
                <div class="info-card-icon work">
                  <feather-icon
                    icon="BriefcaseIcon"
                    size="24"
                  />
                </div>
                <h5 class="info-card-title">
                  Informations Professionnelles
                </h5>
              </div>
              <div class="info-card-body">
                <div class="info-item">
                  <feather-icon
                    icon="HomeIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Entrepôt Assigné</span>
                    <span class="info-value">{{ staff.repository && staff.repository.name ? staff.repository.name : 'Non assigné' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <feather-icon
                    icon="UserCheckIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Rôle</span>
                    <span class="info-value">{{ staff.role || 'Personnel' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Personal Information -->
            <div class="info-card">
              <div class="info-card-header">
                <div class="info-card-icon personal">
                  <feather-icon
                    icon="UserIcon"
                    size="24"
                  />
                </div>
                <h5 class="info-card-title">
                  Informations Personnelles
                </h5>
              </div>
              <div class="info-card-body">
                <div class="info-item">
                  <feather-icon
                    icon="CreditCardIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">CIN</span>
                    <span class="info-value">{{ staff.cin || 'Non défini' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <feather-icon
                    icon="MapPinIcon"
                    size="16"
                    class="info-icon"
                  />
                  <div class="info-content">
                    <span class="info-label">Adresse</span>
                    <span class="info-value">{{ staff.address || 'Non définie' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Status Confirmation Modal -->
    <b-modal
      ref="statusModal"
      :title="statusModalTitle"
      size="md"
      centered
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="handleStatusConfirmed"
    >
      <p class="text-center mb-3">
        {{ statusModalMessage }}
      </p>
      <div class="text-center">
        <feather-icon
          :icon="staff.is_active ? 'AlertTriangleIcon' : 'CheckCircleIcon'"
          size="48"
          :class="staff.is_active ? 'text-warning' : 'text-success'"
        />
      </div>

      <template #modal-ok>
        <feather-icon
          :icon="staff.is_active ? 'XCircleIcon' : 'CheckCircleIcon'"
          size="16"
          class="mr-1"
        />
        {{ staff.is_active ? 'Désactiver' : 'Activer' }}
      </template>

      <template #modal-cancel>
        <feather-icon
          icon="XIcon"
          size="16"
          class="mr-1"
        />
        Annuler
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  BButton,
  BRow,
  BCol,
  BModal,
} from 'bootstrap-vue'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BModal,
  },
  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      staff: {},
      error: null,
      statusModalTitle: '',
      statusModalMessage: '',
    }
  },

  created() {
    this.fetchStaffDetails()
  },

  methods: {
    async fetchStaffDetails() {
      try {
        const { id } = this.$route.params
        // Use the staff members endpoint
        const { data } = await axios.get(`/api/auth/staff-members/${id}/`)
        this.staff = data
        this.isLoading = false
      } catch (error) {
        this.error = 'Erreur lors du chargement des données du membre du personnel'
        this.isLoading = false
        if (error.response && error.response.status === 404) {
          this.$router.push({ name: 'error-404' })
        }
      }
    },

    getInitials(name) {
      if (!name) return 'U'
      return name
        .split(' ')
        .map(part => part.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('')
    },

    formatDate(dateString) {
      if (!dateString) return 'Non définie'
      return format(new Date(dateString), 'dd MMMM yyyy, HH:mm', { locale: fr })
    },

    toggleStaffStatus() {
      this.statusModalTitle = this.staff.is_active ? 'Désactiver le membre' : 'Activer le membre'
      this.statusModalMessage = this.staff.is_active
        ? 'Êtes-vous sûr de vouloir désactiver ce membre du personnel ? Il ne pourra plus accéder à son compte.'
        : 'Êtes-vous sûr de vouloir activer ce membre du personnel ? Il pourra à nouveau accéder à son compte.'
      this.$refs.statusModal.show()
    },

    async handleStatusConfirmed() {
      try {
        await axios.put(`/api/auth/staff-members/${this.staff.id}/deactivate/`)
        this.staff.is_active = !this.staff.is_active
        this.showToast('success', `Le membre a été ${this.staff.is_active ? 'activé' : 'désactivé'} avec succès`)
      } catch (error) {
        this.showToast('danger', 'Erreur lors du changement de statut')
      }
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position: 'top-right',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// Modern Manager Detail Styling with B2C Branding
.manager-detail-modern {
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
}

// Skeleton Loading Styles
.skeleton-loading {
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
}

// Base skeleton animation
@keyframes skeleton-pulse {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

.skeleton-element {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-pulse 1.5s infinite;
  border-radius: 8px;
}

// Skeleton Header
.skeleton-header {
  .skeleton-title {
    @extend .skeleton-element;
    width: 200px;
    height: 40px;
    border-radius: 8px;
  }

  .skeleton-subtitle {
    @extend .skeleton-element;
    width: 300px;
    height: 20px;
    border-radius: 6px;
  }

  .skeleton-actions {
    display: flex;
    gap: 0.5rem;

    .skeleton-btn {
      @extend .skeleton-element;
      width: 120px;
      height: 44px;
      border-radius: 12px;
    }
  }
}

// Skeleton Profile Card
.skeleton-profile-card {
  background: white;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  height: 100%;

  .skeleton-profile-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 2rem;
    text-align: center;
    border-bottom: 1px solid #e9ecef;

    .skeleton-avatar {
      @extend .skeleton-element;
      width: 100px;
      height: 100px;
      border-radius: 50%;
      margin: 0 auto 1.5rem auto;
    }

    .skeleton-name {
      @extend .skeleton-element;
      width: 150px;
      height: 28px;
      margin: 0 auto 0.5rem auto;
      border-radius: 6px;
    }

    .skeleton-role {
      @extend .skeleton-element;
      width: 120px;
      height: 16px;
      margin: 0 auto 1rem auto;
      border-radius: 4px;
    }

    .skeleton-status {
      @extend .skeleton-element;
      width: 80px;
      height: 32px;
      margin: 0 auto;
      border-radius: 16px;
    }
  }

  .skeleton-profile-stats {
    padding: 1.5rem;

    .skeleton-stat-item {
      display: flex;
      align-items: center;
      padding: 1rem 0;
      border-bottom: 1px solid #f1f2f6;

      &:last-child {
        border-bottom: none;
      }

      .skeleton-stat-icon {
        @extend .skeleton-element;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        margin-right: 1rem;
        flex-shrink: 0;
      }

      .skeleton-stat-content {
        flex: 1;

        .skeleton-stat-label {
          @extend .skeleton-element;
          width: 80px;
          height: 12px;
          margin-bottom: 0.5rem;
          border-radius: 4px;
        }

        .skeleton-stat-value {
          @extend .skeleton-element;
          width: 120px;
          height: 16px;
          border-radius: 4px;
        }
      }
    }
  }
}

// Skeleton Information Cards
.skeleton-info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  .skeleton-info-card {
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    &.skeleton-info-card-full {
      @media (min-width: 768px) {
        grid-column: 1 / -1;
      }
    }

    .skeleton-card-header {
      padding: 1.5rem;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-bottom: 1px solid #e9ecef;
      display: flex;
      align-items: center;

      .skeleton-card-icon {
        @extend .skeleton-element;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        margin-right: 1rem;
        flex-shrink: 0;
      }

      .skeleton-card-title {
        @extend .skeleton-element;
        width: 180px;
        height: 20px;
        border-radius: 6px;
      }
    }

    .skeleton-card-body {
      padding: 1.5rem;

      .skeleton-info-item {
        display: flex;
        align-items: flex-start;
        margin-bottom: 1rem;

        &:last-child {
          margin-bottom: 0;
        }

        .skeleton-info-icon {
          @extend .skeleton-element;
          width: 16px;
          height: 16px;
          border-radius: 4px;
          margin-right: 0.75rem;
          margin-top: 0.25rem;
          flex-shrink: 0;
        }

        .skeleton-info-content {
          flex: 1;

          .skeleton-info-label {
            @extend .skeleton-element;
            width: 60px;
            height: 12px;
            margin-bottom: 0.5rem;
            border-radius: 4px;
          }

          .skeleton-info-value {
            @extend .skeleton-element;
            width: 150px;
            height: 16px;
            border-radius: 4px;

            &.skeleton-info-value-long {
              width: 250px;
            }
          }
        }
      }
    }
  }
}

// Page Header Styling
.page-header {
  .page-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }

  .page-subtitle {
    font-size: 1.1rem;
    color: #6c757d;
  }

  .header-actions {
    display: flex;
    gap: 0.5rem;
  }

  .btn-modern {
    background: linear-gradient(135deg, #E31E24 0%, #B91C1C 100%);
    border: none;
    border-radius: 12px;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(227, 30, 36, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(227, 30, 36, 0.4);
    }

    &.btn-modern-outline {
      background: white;
      border: 2px solid #e9ecef;
      color: #6c757d;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

      &:hover {
        border-color: #E31E24;
        background: #E31E24;
        color: white;
      }
    }
  }
}

// Modern Loading Animation
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;

  .loading-content {
    text-align: center;
  }

  .modern-spinner {
    display: inline-block;
    position: relative;
    width: 80px;
    height: 80px;

    .spinner-ring {
      box-sizing: border-box;
      display: block;
      position: absolute;
      width: 64px;
      height: 64px;
      margin: 8px;
      border: 8px solid #E31E24;
      border-radius: 50%;
      animation: spinner-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
      border-color: #E31E24 transparent transparent transparent;

      &:nth-child(1) { animation-delay: -0.45s; }
      &:nth-child(2) { animation-delay: -0.3s; }
      &:nth-child(3) { animation-delay: -0.15s; }
    }
  }

  .loading-text {
    color: #8b92b2;
    font-weight: 500;
    margin-top: 1rem;
  }
}

@keyframes spinner-ring {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// Error State
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;

  .error-content {
    text-align: center;
    max-width: 500px;

    .error-icon {
      color: #E31E24;
      margin-bottom: 1rem;
    }

    .error-title {
      font-size: 1.75rem;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 1rem;
    }

    .error-message {
      color: #6c757d;
      font-size: 1.1rem;
      margin-bottom: 2rem;
    }
  }
}

// Modern Profile Card
.profile-card-modern {
  background: white;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  height: 100%;

  .profile-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 2rem;
    text-align: center;
    border-bottom: 1px solid #e9ecef;

    .profile-avatar {
      position: relative;
      display: inline-block;
      margin-bottom: 1.5rem;

      .avatar-circle-large {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        background: linear-gradient(135deg, #E31E24 0%, #B91C1C 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 10px 30px rgba(227, 30, 36, 0.3);

        .avatar-initials-large {
          color: white;
          font-weight: 700;
          font-size: 2rem;
        }
      }

      .status-indicator-large {
        position: absolute;
        bottom: 5px;
        right: 5px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 4px solid white;
        background: #E31E24;

        &.active {
          background: #16a34a;
        }
      }
    }

    .profile-name {
      font-size: 1.75rem;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 0.5rem;
    }

    .profile-role {
      color: #6c757d;
      font-size: 1rem;
      margin-bottom: 1rem;
    }

    .profile-status {
      .status-badge-large {
        padding: 0.5rem 1.5rem;
        border-radius: 25px;
        font-size: 0.9rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        &.active {
          background: rgba(22, 163, 74, 0.1);
          color: #16a34a;
        }

        &.inactive {
          background: rgba(227, 30, 36, 0.1);
          color: #E31E24;
        }
      }
    }
  }

  .profile-stats {
    padding: 1.5rem;

    .stat-item {
      display: flex;
      align-items: center;
      padding: 1rem 0;
      border-bottom: 1px solid #f1f2f6;

      &:last-child {
        border-bottom: none;
      }

      .stat-icon {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        background: rgba(227, 30, 36, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #E31E24;
        margin-right: 1rem;
      }

      .stat-content {
        flex: 1;

        .stat-label {
          display: block;
          font-size: 0.8rem;
          color: #8b92b2;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.25rem;
        }

        .stat-value {
          display: block;
          font-size: 1rem;
          color: #1f2937;
          font-weight: 600;
        }
      }
    }
  }
}

// Information Cards Grid
.info-cards-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  .info-card {
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    }

    &:nth-child(3) {
      @media (min-width: 768px) {
        grid-column: 1 / -1;
      }
    }

    .info-card-header {
      padding: 1.5rem;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-bottom: 1px solid #e9ecef;
      display: flex;
      align-items: center;

      .info-card-icon {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 1rem;

        &.contact {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        &.work {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        &.personal {
          background: rgba(227, 30, 36, 0.1);
          color: #E31E24;
        }
      }

      .info-card-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: #1f2937;
        margin: 0;
      }
    }

    .info-card-body {
      padding: 1.5rem;

      .info-item {
        display: flex;
        align-items: flex-start;
        margin-bottom: 1rem;

        &:last-child {
          margin-bottom: 0;
        }

        .info-icon {
          color: #8b92b2;
          margin-right: 0.75rem;
          margin-top: 0.25rem;
          flex-shrink: 0;
        }

        .info-content {
          flex: 1;

          .info-label {
            display: block;
            font-size: 0.8rem;
            color: #8b92b2;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 0.25rem;
          }

          .info-value {
            display: block;
            font-size: 1rem;
            color: #1f2937;
            font-weight: 500;
            word-break: break-word;
          }
        }
      }
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  .manager-detail-modern {
    padding: 1rem;
  }

  .skeleton-loading {
    padding: 1rem;
  }

  .page-header {
    .page-title {
      font-size: 2rem;
    }

    .header-actions {
      flex-direction: column;
      width: 100%;
      margin-top: 1rem;

      .btn-modern {
        width: 100%;
        margin-bottom: 0.5rem;
      }
    }
  }

  .skeleton-header {
    .skeleton-actions {
      flex-direction: column;
      width: 100%;
      margin-top: 1rem;

      .skeleton-btn {
        width: 100%;
        margin-bottom: 0.5rem;
      }
    }
  }

  .info-cards-grid {
    grid-template-columns: 1fr;

    .info-card:nth-child(3) {
      grid-column: 1;
    }
  }

  .skeleton-info-grid {
    grid-template-columns: 1fr;

    .skeleton-info-card.skeleton-info-card-full {
      grid-column: 1;
    }
  }
}
</style>
